<template>
  <div class="home-wrapper">
    <!-- Header Banner -->
    <section class="header-banner">
      <div class="container">
        <div class="banner-content">
          <h1 class="banner-title">{{ appTitle }}</h1>
          <p class="banner-subtitle">주차장 정기권 관리 시스템</p>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <section class="main-content">
      <div class="container py-5">
        <div class="row g-4">
          <!-- 왼쪽 섹션 -->
          <div class="col-lg-6">
            <!-- 정기권 신청 (메인 카드) -->
            <div class="card main-card mb-4">
              <div class="card-body text-center py-5">
                <div class="main-icon">📋</div>
                <h3 class="card-title mt-3 mb-2">정기권 신청</h3>
                <p class="card-text text-muted mb-4">
                  원하는 주차장의 정기권을<br />신청해보세요.
                </p>
                <router-link to="/parking-pass-waiting-reg" class="btn btn-primary btn-lg w-100">
                  정기권 신청하기
                </router-link>
              </div>
            </div>

            <!-- 하단 버튼들: 주차장 정보 / 추첨 결과 -->
            <div class="row g-3">
              <!-- 주차장 정보 카드 -->
              <div class="col-md-6">
                <router-link to="/parking-area-info" class="card-link">
                  <div class="card info-card">
                    <div class="card-body">
                      <div class="icon">🅿</div>
                      <h6 class="card-title">주차장 정보</h6>
                      <p class="card-text small">이용요금/시간</p>
                      <span class="link-arrow">바로가기 →</span>
                    </div>
                  </div>
                </router-link>
              </div>
              
              <!-- 추첨 결과 카드 (IsVisibleTicketResult가 켜져있을 때만 표시) -->
              <div v-if="isVisibleTicketResult" class="col-md-6">
                <router-link to="/ticket-result-personal" class="card-link">
                  <div class="card info-card">
                    <div class="card-body">
                      <div class="icon">🎲</div>
                      <h6 class="card-title">추첨 결과</h6>
                      <p class="card-text small">추첨 결과 확인</p>
                      <span class="link-arrow">바로가기 →</span>
                    </div>
                  </div>
                </router-link>
              </div>
            </div>
          </div>

          <!-- 오른쪽 섹션: 공지사항 -->
          <div class="col-lg-6">
            <div class="card notice-section">
              <div class="card-header bg-light">
                <h5 class="mb-0">📢 공지사항</h5>
              </div>
              <div class="card-body p-0">
                <!-- 로딩 -->
                <div v-if="noticeLoading" class="text-center py-4">
                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                    <span class="visually-hidden">조회 중...</span>
                  </div>
                </div>

                <!-- 오류 -->
                <div v-else-if="noticeError" class="alert alert-danger m-3 mb-0">
                  공지사항을 불러올 수 없습니다.
                </div>

                <!-- 공지사항 없음 -->
                <div v-else-if="notices.length === 0" class="text-center text-muted py-4">
                  공지사항이 없습니다.
                </div>

                <!-- 공지사항 목록 -->
                <div v-else class="notice-list">
                  <div
                    v-for="notice in notices.slice(0, 5)"
                    :key="notice.NoticeSeq"
                    class="notice-item"
                    @click="openNoticeDetail(notice)"
                  >
                    <div class="notice-title">{{ notice.Title }}</div>
                    <div class="notice-date">{{ formatDate(notice.CreatedDate) }}</div>
                  </div>
                </div>
              </div>
              <div class="card-footer bg-light text-center">
                <router-link to="/notices" class="btn btn-sm btn-outline-primary">
                  전체 보기
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 공지사항 상세 모달 -->
    <div v-if="noticeDetailModal" class="modal-backdrop-custom">
      <div class="modal-box" @click.stop>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="fw-bold mb-0">{{ selectedNotice.Title }}</h6>
          <button type="button" class="btn-close" @click="closeNoticeDetail"></button>
        </div>

        <div class="border-bottom pb-2 mb-3">
          <small class="text-muted">{{ formatDate(selectedNotice.CreatedDate) }}</small>
        </div>

        <div class="notice-content">
          {{ selectedNotice.Content }}
        </div>

        <div class="d-flex gap-2 justify-content-end mt-4">
          <button type="button" class="btn btn-secondary btn-sm" @click="closeNoticeDetail">닫기</button>
        </div>
      </div>
    </div>
    <!-- Footer -->
    <footer class="site-footer mt-4">
      <div class="container py-3 text-center">
        <div class="mb-2">
          <a href="https://www.efmc.or.kr/fmcs/863" target="_blank" class="btn btn-sm btn-outline-secondary">
            개인정보 처리방침
          </a>
        </div>
        <div class="footer-line">주소 : [03371]서울시 은평구 통일로 684 서울 혁신파크 미래청 4층 </div>
        <div class="footer-line mt-1">사업자등록번호 : 110-82-11907 | FAX : 02-386-5190 | 단속신고 : 010-6440-5255</div>
        <div class="footer-line mt-1 small text-muted">Copyright © 2026 DigitalPie. All Rights Reserved.</div>
      </div>
    </footer>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'HomeView',
  data() {
    return {
      message: '',
      loading: true,
      error: null,
      appTitle: process.env.VUE_APP_TITLE || 'EF Park',
      noticeLoading: false,
      noticeError: null,
      notices: [],
      noticeDetailModal: false,
      isVisibleTicketResult: null,
      selectedNotice: {
        NoticeSeq: null,
        Title: '',
        Content: '',
        CreatedDate: null
      }
    }
  },
  async mounted() {
    try {
      const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/test`)
      this.message = res.data.message
    } catch (e) {
      this.error = '백엔드 연결 실패: ' + e.message
    } finally {
      this.loading = false
    }

    // 공지사항 로드
    this.fetchNotices()
    
    // CommonConfig 로드 (IsVisibleTicketResult 확인)
    this.fetchCommonConfig()
  },
  methods: {
    async fetchNotices() {
      this.noticeLoading = true
      this.noticeError = null
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/notices`, {
          params: { page: 1 }
        })

        if (res.data.success && res.data.notices) {
          this.notices = res.data.notices
        } else {
          this.noticeError = '공지사항을 불러올 수 없습니다.'
        }
      } catch (e) {
        console.error('fetchNotices error:', e)
        this.noticeError = '공지사항 조회 실패'
      } finally {
        this.noticeLoading = false
      }
    },
    async openNoticeDetail(notice) {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/notices/${notice.NoticeSeq}`)
        if (res.data.success && res.data.notice) {
          this.selectedNotice = res.data.notice
          this.noticeDetailModal = true
        }
      } catch (e) {
        console.error('openNoticeDetail error:', e)
      }
    },
    closeNoticeDetail() {
      this.noticeDetailModal = false
      this.selectedNotice = {
        NoticeSeq: null,
        Title: '',
        Content: '',
        CreatedDate: null
      }
    },
    async fetchCommonConfig() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/common-config`)
        if (res.data.success && res.data.config) {
          this.isVisibleTicketResult = res.data.config.IsVisibleTicketResult
        }
      } catch (e) {
        console.error('CommonConfig fetch error:', e)
        // 오류 시에도 기본값 유지
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }
}
</script>

<style scoped>
.home-wrapper {
  background: linear-gradient(180deg, #f5f7fa 0%, #eef2f7 100%);
  min-height: 100vh;
}

/* Header Banner */
.header-banner {
  background: #173d63;
  padding: 8px 20px 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-bottom: 4px solid #ffc107;
  position: relative;
  overflow: hidden;
}

.header-banner::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 38%;
  height: 100%;
  background: repeating-linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.06) 0,
    rgba(255, 255, 255, 0.06) 1px,
    transparent 1px,
    transparent 7px
  );
}

.banner-content {
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 0;
}

.banner-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.1rem;
  letter-spacing: -0.5px;
  line-height: 1.2;
}

.banner-subtitle {
  font-size: 0.8rem;
  color: #555;
  margin-bottom: 0;
  line-height: 1.3;
}

/* Main Content */
.main-content {
  background: linear-gradient(180deg, #f5f7fa 0%, #eef2f7 100%);
}

.container {
  max-width: 1200px;
}

/* 메인 카드 - 정기권 신청 */
.main-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;
  background: white;
  overflow: hidden;
  position: relative;
}

.main-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
}

.main-card:hover {
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
  transform: translateY(-4px);
}

.main-icon {
  font-size: 3.5rem;
  margin-bottom: 1rem;
}

.main-card .card-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a1a1a;
}

.main-card .card-text {
  font-size: 1rem;
  line-height: 1.6;
  color: #6b7280;
}

.main-card .btn {
  border-radius: 8px;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  background-color: #173d63;
  color: #ffffff;
  border: none;
}

.main-card .btn:hover {
  background-color: #0f2846;
  color: #ffffff;
}

/* 정보 카드 - 주차장 정보, 추첨 결과 */
.info-card {
  border: none;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  background: white;
  cursor: pointer;
}

.info-card:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.info-card .card-body {
  text-align: center;
  padding: 1.5rem 1rem;
}

.info-card .icon {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  display: block;
}

.info-card .card-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 0.25rem;
}

.info-card .card-text {
  font-size: 0.85rem;
  color: #9ca3af;
  margin-bottom: 0.75rem;
}

/* Footer */
.site-footer {
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}
.site-footer .footer-line {
  color: #333;
  font-size: 0.9rem;
}
.site-footer .small {
  color: #6c757d;
}

.link-arrow {
  font-size: 0.9rem;
  font-weight: 500;
  color: #667eea;
  display: inline-block;
  transition: all 0.2s ease;
}

.card-link {
  text-decoration: none;
  display: block;
}

.info-card:hover .link-arrow {
  color: #764ba2;
  transform: translateX(2px);
}

/* 공지사항 섹션 */
.notice-section {
  border: none;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.notice-section .card-header {
  border: none;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.notice-section .card-header h5 {
  color: #1a1a1a;
  font-weight: 600;
  margin-bottom: 0;
}

.notice-section .card-body {
  flex: 1;
  overflow-y: auto;
}

.notice-section .card-footer {
  border-top: 1px solid #e5e7eb;
  padding: 0.75rem 1rem;
}

/* 공지사항 목록 */
.notice-list {
  padding: 0.5rem 0;
}

.notice-item {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: all 0.2s ease;
}

.notice-item:last-child {
  border-bottom: none;
}

.notice-item:hover {
  background-color: #f9fafb;
}

.notice-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notice-date {
  font-size: 0.8rem;
  color: #9ca3af;
}

/* 모달 스타일 */
.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-box {
  background: white;
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  padding: 1.5rem;
}

.notice-content {
  white-space: pre-wrap;
  word-wrap: break-word;
  line-height: 1.6;
  color: #1a1a1a;
  font-size: 0.95rem;
}

.btn-close {
  padding: 0.25rem;
}

/* 반응형 */
@media (max-width: 991px) {
  .banner-title {
    font-size: 1.5rem;
  }

  .banner-subtitle {
    font-size: 0.9rem;
  }

  .main-card .card-title {
    font-size: 1.35rem;
  }

  .notice-section {
    margin-top: 2rem;
  }
}
</style>
